import { FC, useContext, useEffect, useRef } from 'react'
import { Box, Typography, useTheme } from '@mui/material'
import { VegaEmbed } from 'react-vega'
import { LatLngTuple } from 'leaflet'
import { MapContainer, TileLayer, Circle, Marker } from 'react-leaflet'
import NodeHandle from '../../components/shared/Handle'
import { AppConfigContext } from '../../contexts/app-config.context'
import { PlaybookContext } from '../../contexts/playbooks.context'
import { nodeIcon } from '../../utils/constants'
import { findNodeType, deg2radians } from '../../utils/functions'
import { SpexNode } from '../../types/playbook.type'

import 'mapbox-gl/dist/mapbox-gl.css'
import styles from './styles.module.scss'

export const nodeLabelStyle: React.CSSProperties = {
  fontSize: '30px',
  fontWeight: '600',
  letterSpacing: '0.00938em',
  fontFamily:
    "-apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue', sans-serif",
}

interface IProps {
  id: string
  type: string
  data: {
    label: string
  }
  selected?: boolean
  width?: number
}

const TextNode: FC<IProps> = ({ id, type, data, selected, width }) => {
  const theme = useTheme()

  const { playbook, nodeTypes, spexNodes, runState } =
    useContext(PlaybookContext)

  const nodeType = findNodeType(type, nodeTypes)
  const NodeIcon = nodeIcon({ icon: nodeType?.icon || '' })

  const { isLightMode } = useContext(AppConfigContext)
  const style: React.CSSProperties = {
    backgroundColor: theme.palette.elevated.main,
    border: '2px solid ' + theme.palette.elevated.dark,
  }

  if (selected) {
    Object.assign(style, {
      boxShadow: '0 0 10px ' + theme.palette.text.primary,
    })
  }

  if (width) {
    style.width = `${width}px`
  }

  let spexNode: SpexNode | undefined
  if (id) {
    const idInt = parseInt(id)
    spexNode = spexNodes[idInt]
  }

  /*
   * Prevent events from propagating to parent elements.
   *
   * We use this to block click and mousedown events on some node views
   * from reaching their ReactFlow elements,
   * because that would trigger opening the Edit panel
   * or starting a drag-and-drop interaction,
   * but the views themselves are intended to be interactive.
   * For example dragging on a map should pan it,
   * and clicking on a Vega chart's "..." button should open the menu.
   *
   * For onClick we can use React's event abstraction,
   * but onMouseDown doesn't work.
   * That's because React only attaches *one* true DOM event listener
   * to the top of the DOM tree (on `<div id="root">`),
   * so it is not able to stop the actual DOM event from propagating.
   * If anything else is using native event listeners (like ReactFlow), it still sees the event.
   * In fact ReactFlow's mousedown handler also calls stopPropagation,
   * so React never even hears about it.
   * Therefore we have to attach a native event listener too.
   * We do that below with useRef.
   */
  const stopPropagation = (event: React.MouseEvent<any> | Event) => {
    event.stopPropagation()
  }

  const viewRef = useRef<HTMLDivElement>(null)
  useEffect(() => {
    const div = viewRef.current

    if (div) {
      div.addEventListener('mousedown', stopPropagation)
    }

    // Remove the listener on cleanup:
    return () => {
      if (div) {
        div.removeEventListener('mousedown', stopPropagation)
      }
    }
  }, [])

  let view = null

  /*
   * For debugging:
  let view = (
    <div ref={viewRef} style={{backgroundColor: 'red', width: '300px', height: '300px'}}
      onClick={stopPropagation} onMouseDown={stopPropagation}
    />
  )
   */

  switch (spexNode?.view) {
    case 'text':
      if (spexNode.type === 'store')
        view = (
          <Typography>
            {spexNode.data.state?.length === 1
              ? runState[spexNode.data.state[0][0]]
              : spexNode.data.state.map(([key]: [string]) => (
                  <>
                    {key} = {runState[key]}
                    <br />
                  </>
                ))}
          </Typography>
        )
      break
    case 'chart':
      // TODO: parameterize the custom_state entry
      // TODO: show a key matching the color to the custom_state entry
      // TODO: separate color for each entry in spexNode.data.state
      // TODO: parameterize this whole graph definition
      if (!spexNode.data.state?.length) break

      const datapoints: { t: number; y: number; c: number }[] = []
      spexNode.data.state.forEach(([key]: [string], c: number) => {
        const struct = runState[key]
        const tss = struct ? runState[struct.tskey] : []
        tss.forEach((ts: number, i: number) => {
          datapoints.push({
            t: ts,
            y: struct.vals[i],
            c,
          })
        })
      })

      const spec = {
        $schema: 'https://vega.github.io/schema/vega/v6.json',
        width: 400,
        height: 200,
        padding: 5,
        signals: [
          {
            name: 'interpolate',
            value: 'linear',
          },
        ],
        data: [
          {
            name: 'table',
            values: datapoints,
          },
        ],
        scales: [
          {
            name: 't',
            type: 'time',
            range: 'width',
            domain: { data: 'table', field: 't' },
          },
          {
            name: 'y',
            type: 'linear',
            range: 'height',
            nice: true,
            zero: false,
            domain: { data: 'table', field: 'y' },
          },
          {
            name: 'color',
            type: 'ordinal',
            range: 'category',
            domain: { data: 'table', field: 'c' },
          },
        ],
        encoding: {
          x: { field: 'date', type: 'temporal', axis: { format: '%b' } },
        },
        axes: [
          {
            orient: 'bottom',
            scale: 't',
            grid: false,
            labelFlush: true,
            labelColor: isLightMode ? 'black' : 'white',
            labelOverlap: true,
            tickCount: { signal: 'ceil(width / 50)' },
            encode: {
              labels: {
                update: {
                  text: {
                    signal: 'timeFormat(toDate(datum.value), "%H:%M")',
                  },
                },
              },
            },
            zindex: 1,
          },
          {
            orient: 'left',
            scale: 'y',
            labelColor: isLightMode ? 'black' : 'white',
          },
        ],
        marks: [
          {
            type: 'group',
            from: {
              facet: {
                name: 'series',
                data: 'table',
                groupby: 'c',
              },
            },
            marks: {
              type: 'line',
              from: { data: 'series' },
              encode: {
                enter: {
                  x: { scale: 't', field: 't' },
                  y: { scale: 'y', field: 'y' },
                  stroke: { scale: 'color', field: 'c' },
                  strokeWidth: { value: 2 },
                },
                update: {
                  interpolate: { signal: 'interpolate' },
                  strokeOpacity: { value: 1 },
                },
                hover: {
                  strokeOpacity: { value: 0.5 },
                },
              },
            },
          },
        ],
      }

      view = <VegaEmbed spec={spec} onClick={stopPropagation} ref={viewRef} />
      break

    case 'map':
      // TODO: Parameterize center, radius, and pin location. Right now this can only be used on a geofence node.
      const center: LatLngTuple = [
        spexNode.data.centerLat,
        spexNode.data.centerLon,
      ]
      const radius = spexNode.data.radius

      // Compute a reasonable zoom level given the radius and latitude (in radians):
      //
      // radius = radius of our circle
      // n = desired number of 256-pixel tiles to show
      // earth = radius of Earth in meters = 6371008
      // zoom = Math.floor(Math.log2(Math.cos(latitude_radians) * 2 * Math.PI * earth * n / radius))
      const earth = 6371008
      const n = 0.5
      const latitude_radians = deg2radians(center[0])
      const zoom = Math.floor(
        Math.log2(
          (Math.cos(latitude_radians) * 2 * Math.PI * earth * n) / radius,
        ),
      )

      const point: LatLngTuple = runState[`__${spexNode.id}_geofence`]

      view = (
        <div
          onClick={stopPropagation}
          ref={viewRef}
          style={{ width: '500px', height: '400px' }}
        >
          <MapContainer
            id={`playbookMap${spexNode.id}`}
            zoom={zoom}
            minZoom={3}
            dragging={true}
            scrollWheelZoom={true}
            doubleClickZoom={true}
            className={styles.leafletContainer}
            attributionControl={false}
            center={center}
            worldCopyJump={false}
            maxBoundsViscosity={1.0}
            maxBounds={[
              [-90, -180],
              [90, 180],
            ]}
          >
            <TileLayer
              noWrap
              url="https://{s}.tile.opentopomap.org/{z}/{x}/{y}.png"
            />
            <Circle center={center} radius={radius} />
            {point && <Marker position={point} />}
          </MapContainer>
        </div>
      )
      break
  }

  return (
    <div id={`${id}`} className={styles.node} style={style}>
      <div className={styles.content}>
        <Box className={styles.header}>
          <NodeIcon
            sx={{ color: theme.palette.primary.main, width: 40, height: 40 }}
          />
          <Typography
            id="nodeIdentifier"
            className={styles.text}
            style={nodeLabelStyle}
          >
            {data.label}
          </Typography>
        </Box>
        {view}
        {playbook && <NodeHandle direction={playbook.direction} />}
      </div>
    </div>
  )
}

export default TextNode
